import {
  ADMIN_LABEL_VARIANT_NAMES,
  type HotelCardProps,
  type LabelVariantValue,
  type MapHotelCardProps,
} from 'src/components/HotelCard/types';
import { i18n } from '@lt/localization';
import type { Coordinates } from 'src/components/YandexMap/types';
import { objToQueryString } from 'src/utils/uriUtils';
import type { SearchType } from 'src/types/search';

import type { ILabel } from '@lt/api/dist/methods/packages/types';

import { addDays, isValid, parseISO } from 'date-fns';
import { formatDate } from '@lt/components/utils/date';
import { pluralizeNights } from 'src/utils/pluralizeUtils';

import type { HotelRendererProps } from './types';

// Функция, которая добавляет в query инфу по фильтрам, реквест айди и серч тайпу
export const buildHotelURL = (
  date: string,
  price: number,
  nights: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filters: any,
  requestId: string,
  link: string,
  searchType: SearchType,
  adults: number,
  fromCity: string,
  fromCountry: string,
  kids?: number,
  kidsAges?: number[],
) => {
  const {
    operators,
    confirmability,
    ltExtras,
    freeCancel,
    meals,
    paymentBenefits,
    travelBenefits,
    directFlight,
  } = filters || {};

  const hotelConfirmabilityFilter =
    confirmability !== '3' ? confirmability : undefined;

  const query = objToQueryString({
    request_id: requestId,
    search_type: searchType,
    offer_price: price,
    offer_nights: nights,
    offer_date: date,
    offer_operators: operators,
    offer_confirmability: hotelConfirmabilityFilter,
    offer_lt_extras: ltExtras,
    offer_free_cancel: freeCancel,
    offer_direct_flight: directFlight,
    offer_meals: meals,
    offer_payment_benefits: [paymentBenefits].join(),
    offer_travel_benefits: [travelBenefits].join(),
    adults,
    kids,
    from: `${fromCity}-${fromCountry}`,
    ...(kids ? { kidsAges } : {}),
  });

  return `${link}${query ? `?${query}` : ''}`;
};

export const getHotelLocation = (city: string, regionName: string) => {
  if (!city && !regionName) return '';

  if (city === regionName || !regionName) return city;

  if (!city) return regionName;

  return `${city}, ${regionName}`;
};

/**
 * Эта функция разделяет лейблы по месту на карточке, где они должны отображаться
 * и приводит данные по лейблам к единому формату
 */
export const getHotelCardLabels = (
  labels: ILabel[],
): {
  innerLabels: LabelVariantValue[];
  headerLabel?: ILabel;
} => {
  const headerLabel = labels.find((label) => label.context?.search === 1);
  const innerLabels: LabelVariantValue[] = labels
    .filter((label) => label.context?.search === 2)
    .map((el) => ({
      id: String(el.id),
      variant: ADMIN_LABEL_VARIANT_NAMES[el.style],
      tooltipText: el.description,
      text: el.title,
    }));

  return {
    headerLabel,
    innerLabels: innerLabels.slice(0, 6),
  };
};

export const getDatesInfo = (
  startDate: string,
  nightsCount: number,
  isShortNight = false,
) => {
  const dateAsDate = parseISO(startDate);
  const formattedStartDate = isValid(dateAsDate)
    ? formatDate(dateAsDate, 'd MMM')
    : startDate;
  const endDate = addDays(new Date(startDate), nightsCount);
  const formattedEndDate = isValid(endDate)
    ? formatDate(endDate, 'd MMM')
    : endDate;

  return `${formattedStartDate} - ${formattedEndDate}, ${nightsCount} ${
    isShortNight
      ? i18n.t('hotelcard.dates.night')
      : pluralizeNights(nightsCount)
  }`;
};

export function getHotelCardProps(
  hotelRendererProps: HotelRendererProps,
): HotelCardProps {
  const {
    images,
    hotelPageVisited,
    bonusCount,
    currencyCode,
    stars,
    name,
    city,
    regionName,
    rating,
    reviewsCount,
    features,
    instantConfirm,
    isFlexDates,
    price,
    labels = [],
    isHotelOnly,
    availability,
    startDate,
    nightsCount,
    link,
    className,
    penalizedFilters = [],
    offerFilters,
    requestId,
    long,
    lat,
    id,
    setActiveHotel,
    adjacentTours,
    openInCurrentTab,
    isMobile,
    activateCashbackForPartner,
    cashbackLabel,
    cashbackIconWebp,
    cashbackIconPng,
    cashbackTooltipText,
    labelColor,
    textColor,
    adults,
    kids,
    kidsAges,
    fromCity,
    fromCountry,
    skiLiftDistance,
  } = hotelRendererProps;

  const onLongHover = () => {
    if (penalizedFilters.length || !long || !lat) {
      return;
    }

    const activeHotelData = {
      id,
      coordinates: [long, lat] as Coordinates,
      visited: hotelPageVisited,
    };

    setActiveHotel(activeHotelData);
  };

  const { innerLabels, headerLabel } = getHotelCardLabels(labels);

  const adjacentToursLimit = isMobile ? 2 : 3;

  return {
    id,
    hotelPageHref: buildHotelURL(
      startDate,
      price,
      nightsCount,
      offerFilters,
      requestId || '',
      link,
      isHotelOnly ? 'hotel' : 'package',
      adults,
      fromCity,
      fromCountry,
      kids,
      kidsAges,
    ),
    skiLiftDistance,
    images,
    hotelPageVisited,
    bonusCount: activateCashbackForPartner ? bonusCount : 0,
    currencyCode,
    stars,
    hotelName: name,
    hotelLocation: getHotelLocation(city, regionName),
    rating,
    reviewsCount,
    features,
    instantConfirm,
    isFlexDates,
    price,
    innerLabels,
    headerLabel,
    isHotelOnly,
    availability,
    openInCurrentTab,
    datesInfo: getDatesInfo(startDate, nightsCount),
    className,
    penalizedFilters,
    onLongHover,
    adjacentTours: adjacentTours?.slice(0, adjacentToursLimit).map((tour) => ({
      ...tour,
      datesInfo: getDatesInfo(tour.startDate, tour.nightsCount, true),
      href: buildHotelURL(
        tour.startDate,
        tour.price,
        tour.nightsCount,
        offerFilters,
        requestId || '',
        link,
        isHotelOnly ? 'hotel' : 'package',
        adults,
        fromCity,
        fromCountry,
        kids,
        kidsAges,
      ),
    })),
    cashbackLabel,
    cashbackIconWebp,
    cashbackIconPng,
    cashbackTooltipText,
    labelColor,
    textColor,
  };
}

export function getMapHotelCardProps(
  hotelRendererProps: HotelRendererProps,
): MapHotelCardProps {
  const {
    id,
    images,
    hotelPageVisited,
    bonusCount,
    currencyCode,
    stars,
    name,
    city,
    regionName,
    reviewsCount,
    rating,
    features,
    instantConfirm,

    price,
    labels = [],
    isFreeCancellationPolicy,
    isHotelOnly,
    availability,

    link,
    className,
    resetPopupData = () => null,
    startDate,
    nightsCount,
    offerFilters,
    requestId,
    openInCurrentTab,

    onCloseMapPopup,
    isExpanded = false,

    activateCashbackForPartner,
    cashbackLabel,
    cashbackIconWebp,
    cashbackIconPng,
    cashbackTooltipText,
    labelColor,
    textColor,
    skiLiftDistance,

    adults,
    kids,
    kidsAges,
    fromCity,
    fromCountry,
  } = hotelRendererProps;

  const { innerLabels, headerLabel } = getHotelCardLabels(labels);

  return {
    id,
    onCloseIconClick: () => {
      resetPopupData();
      onCloseMapPopup?.();
    },
    hotelPageHref: buildHotelURL(
      startDate,
      price,
      nightsCount,
      offerFilters,
      requestId || '',
      link,
      isHotelOnly ? 'hotel' : 'package',
      adults,
      fromCity,
      fromCountry,
      kids,
      kidsAges,
    ),
    images,
    hotelPageVisited,
    bonusCount: activateCashbackForPartner ? bonusCount : 0,
    currencyCode,
    stars,
    skiLiftDistance,
    hotelName: name,
    hotelLocation: getHotelLocation(city, regionName),
    rating,
    reviewsCount,
    features,
    instantConfirm,
    price,
    innerLabels,
    headerLabel,
    isFreeCancellationPolicy,
    isHotelOnly,
    isExpanded,
    availability,
    openInCurrentTab,
    className,
    cashbackLabel,
    cashbackIconWebp,
    cashbackIconPng,
    cashbackTooltipText,
    labelColor,
    textColor,
  };
}
